import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconDisconnect, IconReload } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAlertRoutingAccess } from './access'
import { getConnectProviders, listConnections, startConnect } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ConnectionPill, connectionState, missingScopesOf, newPermissionsText, notConfiguredText, notReadyText, providerLabel, useLeaveForConsent } from './connection-field'
import { useConnectionActions } from './connection-actions'
import { SETUP_PILL, connectionSetupPill } from './setup-words'
import { CONNECTIONS_PAGE, connectionHref } from './paths'

// The providers the catalogue's Connect types connect through (their
// connection field's provider), each with whether one of its types is
// ready (the fleet flag).
export const connectProvidersOf = catalog => {
  const out = new Map()
  catalog.actionTypes.filter(a => a.connect).forEach(a => (a.configSchema || []).filter(f => f.type === 'connection' && f.provider).forEach(f => {
    out.set(f.provider, Boolean(out.get(f.provider)) || a.ready !== false)
  }))
  return out
}

// A workspace name caps at this (px) and ellipsizes, so the columns fit a
// 1280 px window (CLAUDE.md "Column widths"); spare width still widens it.
export const WORKSPACE_MAX = 280

// The cells' words on the row: the grid sorts, quick-filters and exports a
// column's field, so each holds what its cell shows.
export const connectionRow = c => {
  const targets = c.targets || []
  const state = connectionState(c)
  return {
    ...c,
    targetsText: targets.length ? targets.map(t => t.name).join(', ') : 'No targets yet',
    stateKey: state.key,
    statusText: state.label,
    // the status cell's second line: how many permissions wait, else why
    statusLine: state.key === 'needs_approval' ? newPermissionsText(missingScopesOf(c).length) : c.statusReason || '',
    connectedByText: (c.connectedBy && c.connectedBy.name) || ''
  }
}

// Two-line cells (the API keys lists): the workspace over the targets that
// post through it; the status pill over its reason; who connected it over when.
const workspaceCell = r => (r.data
  ? <span className="min-w-0">
      <span className="tb-cell-row">
        <span className="fw-semibold text-truncate" title={r.data.externalName}>{r.data.externalName}</span>
        {connectionSetupPill(r.data, r.data.stateKey) && <Pill tone="warn" title="No targets use this workspace yet.">{SETUP_PILL}</Pill>}
      </span>
      <span className="sw-cell-sub text-truncate" title={r.data.targetsText}>{r.data.targetsText}</span>
    </span>
  : '')
const statusCell = r => (r.data
  ? <span className="min-w-0">
      <span className="tb-cell-row"><ConnectionPill connection={r.data} /></span>
      {r.data.statusLine && <span className="sw-cell-sub text-truncate" title={r.data.statusLine}>{r.data.statusLine}</span>}
    </span>
  : '')
const connectedCell = r => (r.data
  ? <span className="min-w-0">
      <span className="text-truncate">{r.data.connectedByText || '—'}</span>
      {r.data.connectedAt && <span className="sw-cell-sub" title={formatDateTime(r.data.connectedAt)}>{formatAgo(r.data.connectedAt)}</span>}
    </span>
  : '')

// Alert connections (/admin/alert-routing/connections; owner 2026-10-30: its
// own page in the sidebar, no longer a strip at the top of Targets): every
// connected workspace, the targets that post through it, its status and why,
// and who connected it; a workspace no target uses says "Setup incomplete"
// beside its name (setup-words.js). A row opens its page
// (/connections/:connectionID, the address the "Slack needs reconnecting"
// alert links to); its pinned icons are Reconnect and Disconnect (the API keys
// lists' row actions), the same actions as the page's (connection-actions.js). The card sizes to its
// rows (FleetGrid rows): a platform has one or two workspaces.
//
// Connect <provider> is the card's pinned header item (CLAUDE.md "List page
// layout"), offered only where the provider is set up and its type is ready;
// the empty list says why there is nothing, with the same Connect when it can
// be used. A catalogue with no Connect type (an older server) says so. What an
// action did, and a flash from the consent page with nothing pending
// (connect-return.js), is the card's status line.
export const ConnectionsPage = () => {
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const history = useHistory()
  const location = useLocation()
  const { pathname } = location
  const [said, setSaid] = useState(() => (location.state && location.state.flash) || '')
  const ready = connectProvidersOf(catalog)
  const wanted = ready.size > 0
  const [rows, setRows] = useState(undefined) // undefined: loading; null: failed
  const [providers, setProviders] = useState([])
  const [reload, setReload] = useState(0)
  const [error, failure, setError] = useErrorLine()
  const consent = useLeaveForConsent()
  const tableRef = useRef()
  const actions = useConnectionActions({
    onDisconnected: (answer, connection) => {
      setSaid(`${connection.externalName} is disconnected.`)
      setReload(n => n + 1)
    }
  })

  // Read again whenever the address changes.
  useEffect(() => {
    if (!wanted) return undefined
    let live = true
    Promise.all([getConnectProviders(), listConnections()]).then(([p, c]) => {
      if (!live) return
      setError(null)
      setProviders(p || [])
      setRows((c || []).map(connectionRow))
    }, err => {
      if (!live) return
      setRows(null)
      setError(serverMessage(err, "The connected workspaces couldn't be loaded."), err)
    })
    return () => { live = false }
  }, [wanted, pathname, reload, setError])

  // The flash is shown once: the address keeps none of it.
  useEffect(() => {
    if (location.state && location.state.flash) history.replace({ pathname: location.pathname, search: location.search })
  }, [location, history])

  const gridList = useGridList(rows === null ? [] : rows)
  const offers = [...ready.keys()].map(provider => {
    const known = providers.find(p => p.provider === provider)
    const label = providerLabel(provider, providers)
    const blocked = !(known && known.configured) ? notConfiguredText(label) : !ready.get(provider) ? notReadyText(label) : null
    const go = () => consent.leave(() => startConnect(provider, { scope: 'platform' }), { provider, purpose: 'connect', returnTo: CONNECTIONS_PAGE },
      `${label} couldn't be reached. Try again.`)
    return { provider, label, blocked, go }
  })
  const usable = offers.filter(o => !o.blocked && canEdit)
  const menu = usable.map(o => <MenuItem key={o.provider} Icon={IconAdd} text={`Connect ${o.label}`} pin keepOnMobile show disabled={consent.busy} onClick={o.go} />)

  const { reconnect, askDisconnect } = actions
  const leaving = consent.busy || actions.consent.busy
  const rowMenu = useMemo(() => c => (
    <Menu small>
      <MenuItem Icon={IconReload} text="Reconnect" pin show={canEdit} disabled={leaving}
        title={c.stateKey === 'needs_approval' ? 'Approve the new permissions' : undefined} onClick={() => reconnect(c)} />
      <MenuItem Icon={IconDisconnect} text="Disconnect" pin show={canEdit && c.status !== 'disconnected'} onClick={() => askDisconnect(c)} />
    </Menu>
  ), [canEdit, leaving, reconnect, askDisconnect])

  const page = children =>
    <FleetPage title="Alert connections" purpose="The Slack workspaces alerts can post to.">
      {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
      {children}
    </FleetPage>
  if (!catalog.loaded) return page(<p className="sw-hint" role="status">Loading…</p>)
  if (!wanted) return page(<Notice tone="info">No connection type is available on this environment yet.</Notice>)
  const first = offers[0]
  const emptyMessage = first && first.blocked
    ? first.blocked
    : `No ${first ? first.label : ''} workspace is connected yet.`
  const failedAction = consent.error ? consent : actions.consent.error ? actions.consent : null

  return page(
    <>
      {error
        ? <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>
        : <FleetGrid title="Workspaces" tableRef={tableRef} compact rows={(rows || []).length} rowHeight={ROW_HEIGHT_TWO_LINE} menu={menu}
            notice={failedAction
              ? <span className="text-danger" role="alert">{failedAction.error} <ErrorReference error={failedAction.failure} compact /></span>
              : said ? <span className="sw-status" role="status">{said}</span> : null}>
            <TableAgClient ref={tableRef} queryKey="connections" apiList={gridList} rowHeight={ROW_HEIGHT_TWO_LINE}
              onRowClicked={c => history.push(connectionHref(c.id))}
              emptyState={{ message: emptyMessage, action: usable[0] ? { label: `Connect ${usable[0].label}`, Icon: IconAdd, onClick: usable[0].go } : undefined }}>
              <ColumnFormat field="externalName" header="Workspace" sortable stacked flex={2} textMax={WORKSPACE_MAX} render={workspaceCell} />
              <ColumnFormat field="statusText" header="Status" sortable stacked flex={1} textMax={WORKSPACE_MAX} render={statusCell} />
              <ColumnFormat field="connectedByText" header="Connected by" sortable stacked fit="content" render={connectedCell} />
              {canEdit && <ColumnMenu menu={rowMenu} />}
            </TableAgClient>
          </FleetGrid>}
      {actions.dialogs}
    </>
  )
}
